import type { ReactNode } from 'react';

import { cn } from '@src/design-system/helpers';
import { REMINDER_CHANNELS } from '@src/shared/types/reminder';
import type { TMedia } from '@src/shared/types/media';

import { EmailPreview } from './email-preview';
import { SmsPreview } from './sms-preview';
import { WhatsAppPreview } from './whatsapp-preview';

export type LivePreviewDeviceProps = {
  className?: string;
  channel?: string;
  content?: string;
  subject?: string;
  attachments?: TMedia[];
};

function normalizeChannel(raw?: string) {
  const v = String(raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_');
  if (v === REMINDER_CHANNELS.email) return REMINDER_CHANNELS.email;
  if (v === REMINDER_CHANNELS.sms) return REMINDER_CHANNELS.sms;
  if (v === REMINDER_CHANNELS.whatsapp) return REMINDER_CHANNELS.whatsapp;
  return null;
}

function PhoneFrame({ children }: { children: ReactNode }) {
  return (
    <div className='mx-auto flex justify-center px-2 py-3'>
      <section
        aria-label='Phone preview'
        className='relative box-border flex h-[650px] w-[300px] shrink-0 flex-col overflow-hidden rounded-[2.5rem] border-[0px] border-black bg-black shadow-xl'
      >
        <div
          className='pointer-events-none absolute left-1/2 top-2 z-10 h-[22px] w-[120px] -translate-x-1/2 rounded-b-[14px] bg-black'
          aria-hidden
        />
        <div className='mt-[10px] flex min-h-0 flex-1 flex-col px-[7px] pb-[10px] pt-0'>
          <div className='pointer-events-none flex min-h-0 flex-1 flex-col overflow-hidden rounded-[2rem] bg-white'>
            {children}
          </div>
        </div>
      </section>
    </div>
  );
}

export function LivePreviewDevice({
  className,
  channel,
  content,
  subject,
  attachments,
}: LivePreviewDeviceProps) {
  const ch = normalizeChannel(channel);

  return (
    <div
      className={cn(
        'flex max-h-full min-h-0 w-full flex-col overflow-auto overscroll-contain',
        className
      )}
    >
      <PhoneFrame>
        {ch === REMINDER_CHANNELS.email && (
          <EmailPreview
            attachments={attachments}
            content={content}
            subject={subject}
            className='h-full min-h-0'
          />
        )}

        {ch === REMINDER_CHANNELS.sms && (
          <SmsPreview
            attachments={attachments}
            className='h-full min-h-0'
            content={content}
          />
        )}
        {ch === REMINDER_CHANNELS.whatsapp && (
          <WhatsAppPreview
            attachments={attachments}
            className='h-full min-h-0'
            content={content}
          />
        )}
      </PhoneFrame>
    </div>
  );
}
